import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../utils/ThemeSwitcher';
import {
  Lock, Eye, EyeOff, ChevronRight, AlertTriangle, CheckCircle2,
  Moon, Sun
} from 'lucide-react';
import logoSource from '../assets/dataquadio.png';
import logoSourceInverted from '../assets/dataquadio_inverted.png';
import './DataQualityDetectiveLogin.css';

// API Base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.dataquadapp.com';

const ResetPassword = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const token = new URLSearchParams(window.location.search).get('token') || '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Load Outfit font (same as login page)
  useEffect(() => {
    if (!document.querySelector('link[href*="Outfit"]')) {
      const link = document.createElement('link');
      link.href = 'https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap';
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError('This reset link is missing its token. Please request a new one.');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match. Please re-enter them.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: newPassword }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Could not reset password.');
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Could not reset password. Please request a new link.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dql-root" data-theme={isDarkMode ? 'dark' : 'light'}>
      {/* Ambient backdrop */}
      <div className="dql-backdrop" aria-hidden="true">
        <div className="dql-glow dql-glow-1" />
        <div className="dql-glow dql-glow-2" />
      </div>

      <div className="dql-panels">
        <div className="dql-left">
          <div className="dql-form-container">
            {/* Brand */}
            <div className="dql-logo-section">
              <div className="dql-logo-wrapper">
                <img
                  src={isDarkMode ? logoSourceInverted : logoSource}
                  alt="dataQuad"
                  className="dql-logo-image"
                />
              </div>
              <h1 className="dql-brand-title">dataQuad</h1>
              <div className="dql-pill">
                <span className="dql-pill-dot" />
                <span>Secure sign-in</span>
              </div>
            </div>

            {/* Card */}
            <div className="dql-card">
              <div className="dql-card-header">
                <h2 className="dql-card-title">
                  {success ? 'Password reset' : 'Set a new password'}
                </h2>
                <p className="dql-card-subtitle">
                  {success
                    ? 'Your password has been updated. You can now sign in.'
                    : 'Choose a new password for your account.'}
                </p>
              </div>

              {error && (
                <div className="dql-error" role="alert">
                  <AlertTriangle size={16} />
                  <span>{error}</span>
                </div>
              )}

              {success ? (
                <div className="dql-switch" style={{ marginTop: '8px' }}>
                  <button
                    type="button"
                    onClick={() => navigate('/login')}
                    className="dql-btn-submit"
                  >
                    <CheckCircle2 size={18} />
                    <span>Go to sign in</span>
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="dql-form">
                  <div className="dql-field">
                    <Lock size={18} className="dql-input-icon" />
                    <input
                      className="dql-input"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="New password (min. 8 characters)"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      autoComplete="new-password"
                      minLength={8}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="dql-password-toggle"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>

                  <div className="dql-field">
                    <Lock size={18} className="dql-input-icon" />
                    <input
                      className="dql-input"
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      autoComplete="new-password"
                      required
                      aria-invalid={confirmPassword.length > 0 && confirmPassword !== newPassword}
                      style={
                        confirmPassword.length > 0 && confirmPassword !== newPassword
                          ? { borderColor: '#EF4444' }
                          : undefined
                      }
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="dql-password-toggle"
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    >
                      {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {confirmPassword.length > 0 && confirmPassword !== newPassword && (
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '6px',
                      fontSize: '12.5px', color: '#EF4444',
                      marginTop: '-6px', marginBottom: '2px',
                    }} role="status">
                      <AlertTriangle size={13} />
                      <span>Passwords do not match</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    className="dql-btn-submit"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <div className="dql-spinner" />
                        <span>Please wait…</span>
                      </>
                    ) : (
                      <>
                        <span>Reset password</span>
                        <ChevronRight size={18} />
                      </>
                    )}
                  </button>

                  <div className="dql-switch">
                    <p className="dql-switch-text">
                      Remembered it?
                      <button
                        type="button"
                        onClick={() => navigate('/login')}
                        className="dql-switch-btn"
                      >
                        Back to sign in
                      </button>
                    </p>
                  </div>
                </form>
              )}
            </div>

            <div className="dql-footer">
              By continuing, you agree to our{' '}
              <a href="/terms" className="dql-footer-link">Terms of Service</a>{' '}
              and{' '}
              <a href="/privacy" className="dql-footer-link">Privacy Policy</a>.
            </div>

            <button
              onClick={() => {
                const globalToggle = document.querySelector('#global-theme-toggle button');
                if (globalToggle) globalToggle.click();
              }}
              className="dql-theme-toggle"
              title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDarkMode ? <Sun size={13} /> : <Moon size={13} />}
              {isDarkMode ? 'Light' : 'Dark'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
